import React, { useMemo, useState } from 'react';
import { DataGridPro, GridColDef, GridSortModel, GridRowParams } from '@mui/x-data-grid-pro';
import { Box, Typography } from '@mui/material';
import { BenefitType } from '../use-get-benefit-types';
import { FasitDataGridToolbar } from '../../../../../components/data-grid/fasit-data-grid-toolbar';
import { InterventionObjectiveType, InterventionObjectiveModel } from '../../../../../api-service/clients';
import { formatDate } from '../../../../../utilities';

interface InterventionObjectivesTableProps {
  assignmentId?: string;
  interventionObjectives: InterventionObjectiveModel[];
  isLoading: boolean;
  benefitTypes: BenefitType[];
  onCreateNewClick?: () => void;
  onRowClick?: (interventionObjectiveId: string) => void;
  isEditMode?: boolean;
}

function mapObjectiveType(type: InterventionObjectiveType): string {
  switch (type) {
    case InterventionObjectiveType.Develop:
      return 'Udvikle funktionsevne';
    case InterventionObjectiveType.Maintain:
      return 'Fastholde funktionsevne';
    case InterventionObjectiveType.LimitLoss:
      return 'Begrænse tab af funktionsevne';
    default:
      return `${type}`;
  }
}

export function InterventionObjectivesTable(props: InterventionObjectivesTableProps) {
  const { assignmentId, interventionObjectives, isLoading, benefitTypes, onCreateNewClick, onRowClick, isEditMode = false } = props;

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'title',
      sort: 'asc',
    },
  ]);

  // eslint-disable-next-line func-style
  const CustomToolbar = (): React.ReactNode =>
    FasitDataGridToolbar({
      title: 'Indsatsmål',
      showColumnButton: false,
      showFilterButton: false,
      showDensityButton: false,
      showExportButton: false,
      onAddButtonClick: isEditMode ? onCreateNewClick : undefined,
    });

  function getBenefitTypeName(id?: string) {
    if (!id) return '';
    return benefitTypes.find(x => x.id === id)?.displayName || '';
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Titel',
        flex: 1,
        minWidth: 200,
        valueGetter: params => params.row.title || '',
        sortable: true,
      },
      {
        field: 'interventionObjectiveType',
        headerName: 'Måltype',
        flex: 1,
        minWidth: 180,
        valueGetter: params => mapObjectiveType(params.row.interventionObjectiveType),
        sortable: true,
      },
      {
        field: 'functionalAbilityCatalogName',
        headerName: 'Primært udredningstema',
        flex: 1,
        minWidth: 200,
        valueGetter: params => params.row.functionalAbilityCatalogName || '',
        sortable: true,
      },
      {
        field: 'currentFunctionalAbilityCatalogRating',
        headerName: 'Aktuelt funktionsniveau',
        flex: 0.8,
        minWidth: 120,
        valueGetter: params => params.row.currentFunctionalAbilityCatalogRating ?? '',
        sortable: true,
      },
      {
        field: 'expectedFunctionalAbilityCatalogRating',
        headerName: 'Forventet funktionsniveau',
        flex: 0.8,
        minWidth: 120,
        valueGetter: params => params.row.expectedFunctionalAbilityCatalogRating ?? '',
        sortable: true,
      },
      {
        field: 'followUpDate',
        headerName: 'Eventuel separat opfølgningsdato',
        flex: 1,
        minWidth: 160,
        valueGetter: params => (params.row.followUpDate ? new Date(params.row.followUpDate).getTime() : null),
        valueFormatter: params => (params.value ? formatDate(new Date(params.value as number)) : ''),
        type: 'date',
        sortable: true,
      },
    ],
    [],
  );

  const rows = useMemo(
    () =>
      (interventionObjectives ?? []).map((x, index) => ({
        ...x,
        id: x.interventionObjectiveId?.value || `intervention-objective-${index}`,
      })),
    [interventionObjectives],
  );

  const getDetailPanelContent = React.useCallback(
    (params: GridRowParams) => {
      const objective = params.row as InterventionObjectiveModel;
      const otherThemes = (objective.otherFunctionalAbilityCatalogIds || [])
        .map(id => getBenefitTypeName(id))
        .filter(name => name !== '')
        .join(', ');

      return (
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <Box mb={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Målformulering
            </Typography>
            <Typography variant="body2">{objective.description || 'Ingen beskrivelse'}</Typography>
          </Box>
          {otherThemes && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Andre relaterede udredningstemaer
              </Typography>
              <Typography variant="body2">{otherThemes}</Typography>
            </Box>
          )}
        </Box>
      );
    },
    [benefitTypes],
  );

  const getDetailPanelHeight = React.useCallback(() => 'auto' as const, []);

  const noRowsLabel = assignmentId ? 'Ingen indsatsmål' : 'Indsatsmål kan tilføjes når bestillingen er oprettet';

  return (
    <DataGridPro
      rows={rows}
      columns={columns}
      getRowId={row => row.id}
      autoHeight
      disableColumnMenu={false}
      disableSelectionOnClick
      hideFooterSelectedRowCount
      components={{
        Toolbar: CustomToolbar,
      }}
      localeText={{
        noRowsLabel,
      }}
      sortModel={sortModel}
      onSortModelChange={model => setSortModel(model)}
      onRowClick={params => {
        if (onRowClick && params.row.interventionObjectiveId?.value) {
          onRowClick(params.row.interventionObjectiveId.value);
        }
      }}
      loading={isLoading}
      sx={{
        '& .MuiDataGrid-row': {
          cursor: onRowClick ? 'pointer' : 'default',
        },
      }}
      density="compact"
      showCellRightBorder
      showColumnRightBorder
      disableVirtualization
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
    />
  );
}
